/////////////////////////////////////////////////////////////////////
// Documents collected on the user profile (vetting / onboarding). //
/////////////////////////////////////////////////////////////////////

// These are NOT listing fields and NOT Console user fields. Documents are
// stored as `privateFileAttachments` on the user (currentUser has a
// `privateFileAttachments` relationship, settable via the
// /current_user/update_profile endpoint). That scope is readable by the user
// and by operators only — exactly what vetting evidence needs, and why this
// is not a public "certifications" gallery.
//
// Sharetribe's attachment list is FLAT: it stores fileIds and nothing else,
// no slot and no label. So the "which document is this" mapping has to live
// beside it in the user's privateData, keyed by the `key` values below.
// See DOCUMENTS_PRIVATE_DATA_KEY.

export const ALL_COUNTRIES = 'all';

// privateData key holding { [documentKey]: fileId } so uploads can be matched
// back to the slot they were uploaded into.
export const DOCUMENTS_PRIVATE_DATA_KEY = 'documentSlots';

/**
 * Form 1 §08 "Supporting Documents" and Form 2 §07 "Documents" of
 * docs/source-docs/pfs-registration-forms-2026-09.md.
 *
 * - `countries`: ALL_COUNTRIES, or an array of `country` values it applies to.
 * - `userTypes`: which user type sees the slot.
 * - `isOptional`: does not block "submit for vetting".
 * - `supersedesGeneral`: this is a country's localised equivalent of the
 *   general registration set, so the general slots are hidden when it applies.
 *
 * WHAT CHANGED, and why it is a removal rather than an addition:
 *
 * The superseded document (docs/source-docs/Service Provider Vetting Form.txt,
 * Section J) listed per-country registration paperwork for Ethiopia, Kenya,
 * Senegal and Tanzania, plus a tax certification slot. The 2026-09 document
 * asks for none of that: §08 is one country-agnostic list whose first entry,
 * "Certificate of Incorporation / Business Registration", is the generic form
 * of exactly those country sets. Asking a Kenyan provider for a CR12 that the
 * current vetting form does not mention is the kind of drift the client has
 * been reporting, so the country sets are gone.
 *
 * The `countries` / `supersedesGeneral` machinery is deliberately KEPT even
 * though every slot below is now ALL_COUNTRIES: it is tested, it costs
 * nothing, and re-introducing a localised set is then a data change rather
 * than a code change.
 *
 * Files already uploaded into a slot that no longer exists are not lost —
 * documentsWithOrphans() keeps a filled slot on screen (forced optional) so a
 * member can still see and remove the file.
 */
export const profileDocuments = [
  // --- Service provider: Form 1 §08, required uploads ---------------------
  {
    key: 'certificateOfIncorporation',
    label: 'Certificate of Incorporation / Business Registration',
    countries: ALL_COUNTRIES,
    userTypes: ['service-provider'],
    isGeneralRegistration: true,
  },
  {
    key: 'providerCompanyProfile',
    label: 'Company Profile or Brochure',
    countries: ALL_COUNTRIES,
    userTypes: ['service-provider'],
  },
  {
    key: 'providerCv',
    label: 'CV / Profile of Key Technical Personnel',
    countries: ALL_COUNTRIES,
    userTypes: ['service-provider'],
  },
  {
    key: 'providerCompanyLogo',
    label: 'Company Logo',
    countries: ALL_COUNTRIES,
    userTypes: ['service-provider'],
  },

  // --- Service provider: Form 1 §08, optional uploads ---------------------
  // "Make certification upload optional for onboarding" (client feedback
  // 2026-09-16, item 5) — and the document agrees: Certifications /
  // Accreditations is under "Optional uploads", not "Required uploads".
  {
    key: 'providerCertifications',
    label: 'Certifications / Accreditations',
    countries: ALL_COUNTRIES,
    userTypes: ['service-provider'],
    isOptional: true,
  },
  {
    key: 'caseStudies',
    label: 'Case Studies',
    countries: ALL_COUNTRIES,
    userTypes: ['service-provider'],
    isOptional: true,
  },
  {
    key: 'clientReferences',
    label: 'Client References',
    countries: ALL_COUNTRIES,
    userTypes: ['service-provider'],
    isOptional: true,
  },

  // --- Food business: Form 2 §07 ------------------------------------------
  // Both optional — §07 has an "Optional uploads" heading and no required
  // one. The old required set (product catalogue, certifications, photos)
  // came from the superseded document and is not asked for any more, which
  // also settles feedback item 5 on this side of the marketplace.
  {
    key: 'companyProfile',
    label: 'Company Profile',
    countries: ALL_COUNTRIES,
    userTypes: ['food-processor'],
    isOptional: true,
  },
  {
    key: 'companyLogo',
    label: 'Company Logo',
    countries: ALL_COUNTRIES,
    userTypes: ['food-processor'],
    isOptional: true,
  },
];

const appliesToCountry = (doc, country) =>
  doc.countries === ALL_COUNTRIES || (!!country && doc.countries.includes(country));

/**
 * Which document slots to show a given user, in display order.
 *
 * POLICY — chosen here because the vetting document doesn't settle it. Each of
 * these is a defensible reading, not a fact; confirm with the client:
 *
 *  1. Driven by `country` (where the provider is legally based), NOT
 *     `countriesServed`. Registration and tax paperwork attaches to where the
 *     entity is incorporated, not everywhere it happens to work — otherwise a
 *     provider serving nine countries would owe nine sets of filings.
 *  2. A country-specific set REPLACES the general one. Kenya's "Certificate of
 *     Incorporation / Business Registration" plus "KRA PIN" is plainly the
 *     Kenyan form of the general incorporation/registration/tax trio, not
 *     three extra documents on top of it. Same for Ethiopia, Senegal, Tanzania.
 *  3. Countries with no specific list (Ghana, Nigeria, Rwanda, Uganda, Malawi,
 *     South Africa, Zambia, Ivory Coast) fall back to the general set. Ghana
 *     and Nigeria being absent from the document is conspicuous given they head
 *     the countries-served list — likely an omission the client still owes you.
 *  4. `isOptional` slots never block; everything else is required for the
 *     profile to count as complete. There is no publish gate on a user profile
 *     the way there is on a listing, so "required" gates a submit-for-vetting
 *     action rather than the form itself.
 *
 * @param {Object} params
 * @param {string} params.userType 'service-provider' | 'food-processor'
 * @param {string} params.country  the user's `country` value
 * @returns {Array<Object>} entries from `profileDocuments`, in display order
 */
export const documentsForUser = ({ userType, country }) => {
  const forUserType = profileDocuments.filter(doc => doc.userTypes.includes(userType));
  const applicable = forUserType.filter(doc => appliesToCountry(doc, country));
  const hasLocalRegistrationSet = applicable.some(doc => doc.supersedesGeneral);

  const visible = hasLocalRegistrationSet
    ? applicable.filter(doc => !doc.isGeneralRegistration)
    : applicable;

  // Required first, declaration order preserved within each group. Without this
  // the optional "Case studies" entry leads the list for every country that has
  // its own registration set, since it is declared above them.
  return [...visible].sort((a, b) => (a.isOptional ? 1 : 0) - (b.isOptional ? 1 : 0));
};

/**
 * Required slots that have no verified file yet.
 *
 * @param {Array<Object>} documents result of documentsForUser
 * @param {Object} slots privateData[DOCUMENTS_PRIVATE_DATA_KEY], { [key]: fileId }
 * @returns {Array<Object>} the unmet required documents
 */
export const missingRequiredDocuments = (documents, slots = {}) =>
  documents.filter(doc => !doc.isOptional && !slots?.[doc.key]);

/**
 * The applicable documents, plus any slot that still holds a file but is no
 * longer part of the applicable set.
 *
 * Changing country changes which slots apply, and a file uploaded under the
 * old set would otherwise become invisible: still attached to the user, still
 * counted against storage, still shown to an operator in Console — but with no
 * row in the UI, so the member could neither see nor delete it. Keeping a
 * filled slot on screen means a file never disappears without someone choosing
 * to remove it.
 *
 * Orphans are forced optional so they never block completion — they are not
 * required any more, they are just still there.
 *
 * @param {Array<Object>} documents result of documentsForUser
 * @param {Object} slots privateData[DOCUMENTS_PRIVATE_DATA_KEY]
 * @returns {Array<Object>} documents followed by any orphaned slots
 */
export const documentsWithOrphans = (documents, slots = {}) => {
  const shown = new Set(documents.map(doc => doc.key));
  const orphans = profileDocuments
    .filter(doc => !shown.has(doc.key) && !!slots?.[doc.key])
    .map(doc => ({ ...doc, isOptional: true, isOrphaned: true }));

  return [...documents, ...orphans];
};
